import { createListenerMiddleware } from "@reduxjs/toolkit";
import {
  autoSelectFirstStation,
  fetchStations,
  NO_ATC_FILES_MESSAGE_PREFIX,
  refreshStationScenarios,
  selectStation,
} from "../atc/atcSlice";
import type { RootState } from "..";
import { Logger } from "@/app/utils/logger";
import type { AtcStation } from "@/types/AtcManifest";
import {
  showAtcStationFallbackToast,
  showAtcStationUnavailableToast,
} from "@/app/utils/atcToasts";

const LOG_TAG = 'atc-listener';

const normalizeStationKey = (value: string) => value.trim().toLowerCase();

const isSameStation = (left: AtcStation, right: AtcStation) =>
  normalizeStationKey(left.airportCode) === normalizeStationKey(right.airportCode) &&
  normalizeStationKey(left.stationType) === normalizeStationKey(right.stationType);

const buildStationToastId = (station: AtcStation, suffix: string) =>
  `atc-${suffix}-${normalizeStationKey(station.airportCode)}-${normalizeStationKey(station.stationType)}`;

const maybeShowFallbackToast = (requestedStation: AtcStation, activeStation: AtcStation | null) => {
  if (!activeStation || isSameStation(requestedStation, activeStation)) {
    return;
  }

  showAtcStationFallbackToast({
    unavailableStationName: requestedStation.displayName,
    fallbackStationName: activeStation.displayName,
    toastId: buildStationToastId(requestedStation, 'fallback'),
  });
};

const maybeShowUnavailableToast = (requestedStation: AtcStation, message: string | undefined) => {
  if (!message || !message.startsWith(NO_ATC_FILES_MESSAGE_PREFIX)) {
    return;
  }

  showAtcStationUnavailableToast({
    stationName: requestedStation.displayName,
    message,
    toastId: buildStationToastId(requestedStation, 'unavailable'),
  });
};

const logStationSelectionOutcome = (
  actionType: string,
  requestedStation: AtcStation,
  state: RootState
) => {
  const activeStation = state.atc.selectedStation;
  maybeShowFallbackToast(requestedStation, activeStation);

  Logger.debug(`${actionType} - station selection resolved`, LOG_TAG, {
    requestedStationCode: `${requestedStation.airportCode}:${requestedStation.stationType}`,
    activeStationCode: activeStation
      ? `${activeStation.airportCode}:${activeStation.stationType}`
      : 'null',
    hasCurrentScenario: !!state.atc.currentScenario,
    currentScenarioId: state.atc.currentScenario?.scenario_id,
    currentScenarioUrl: state.atc.currentScenario?.cdn_url,
    scenariosCount: state.atc.selectedStation?.scenarios?.length ?? 0,
  });
};

export const listenerMiddleware = createListenerMiddleware()

listenerMiddleware.startListening({
  actionCreator: fetchStations.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const { stations, isBackground, skipped } = action.payload;

    // Skip processing for background refreshes or skipped requests
    // Background updates are handled merge-safe by the reducer
    if (isBackground || skipped) {
      Logger.debug('fetchStations.fulfilled: background/skipped, no listener action needed', LOG_TAG, {
        isBackground,
        skipped,
        stationsCount: stations.length,
      });
      return;
    }

    Logger.debug('fetchStations.fulfilled listener triggered', LOG_TAG, {
      hasSelectedStation: !!state.atc.selectedStation,
      selectedStationCode: state.atc.selectedStation?.airportCode,
      hasCurrentScenario: !!state.atc.currentScenario,
      currentScenarioId: state.atc.currentScenario?.scenario_id,
      hasScenarios: !!state.atc.selectedStation?.scenarios,
      scenariosLength: state.atc.selectedStation?.scenarios?.length,
      stationsCount: stations.length,
    });

    // If a station was persisted but we don't have a current scenario, we need to
    // (re-)select the station to load scenarios and auto-select one.
    // This handles two cases:
    // 1. Scenarios list is empty/null (need to load from CDN)
    // 2. Scenarios list exists (from persist race condition) but currentScenario is not set
    if (state.atc.selectedStation && !state.atc.currentScenario) {
      const { airportCode, stationType } = state.atc.selectedStation;
      const matchedStation = stations.find(
        (s) =>
          s.airportCode.toLowerCase() === airportCode.toLowerCase() &&
          s.stationType.toLowerCase() === stationType.toLowerCase()
      );

      Logger.debug('Checking if need to reload scenarios for persisted station', LOG_TAG, {
        airportCode,
        stationType,
        foundMatchedStation: !!matchedStation,
        scenariosNullOrEmpty: !state.atc.selectedStation.scenarios || state.atc.selectedStation.scenarios.length === 0,
      });

      // Always re-select the station if we don't have a currentScenario.
      // This ensures we load fresh scenarios from CDN and auto-select one.
      if (matchedStation) {
        Logger.info('Reloading scenarios for persisted station (currentScenario missing)', LOG_TAG, { station: matchedStation.displayName });
        try {
          const result = await listenerApi.dispatch(selectStation(matchedStation));
          Logger.debug('selectStation dispatch completed', LOG_TAG, {
            resultType: result.type,
            hasPayload: !!result.payload,
            scenariosLoaded: (result.payload as { scenarios?: unknown[] } | undefined)?.scenarios?.length ?? 0,
          });
        } catch (error) {
          Logger.error('selectStation dispatch failed', LOG_TAG, error);
        }
      } else {
        Logger.warn('Persisted station not found in manifest, cannot restore', LOG_TAG, {
          airportCode,
          stationType,
          availableStations: stations.map(s => `${s.airportCode}:${s.stationType}`),
        });
      }
      return;
    }

    if (state.atc.selectedStation || stations.length === 0) {
      Logger.debug('Skipping auto-select', LOG_TAG, {
        hasSelectedStation: !!state.atc.selectedStation,
        stationsEmpty: stations.length === 0,
      });
      return;
    }

    Logger.info('Auto-selecting first station', LOG_TAG);
    await listenerApi.dispatch(autoSelectFirstStation());
  },
});

// Debug listener: track selectStation completion
listenerMiddleware.startListening({
  actionCreator: selectStation.fulfilled,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    logStationSelectionOutcome(action.type, action.meta.arg, state);
  },
});

listenerMiddleware.startListening({
  actionCreator: refreshStationScenarios.fulfilled,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    logStationSelectionOutcome(action.type, action.meta.arg, state);
  },
});

listenerMiddleware.startListening({
  actionCreator: selectStation.rejected,
  effect: (action) => {
    maybeShowUnavailableToast(action.meta.arg, action.payload as string | undefined);
  },
});

listenerMiddleware.startListening({
  actionCreator: refreshStationScenarios.rejected,
  effect: (action) => {
    maybeShowUnavailableToast(action.meta.arg, action.payload as string | undefined);
  },
});